import { useEffect, useState } from "react";

import { ErrorApi, listarRevisionesFlyer, revertirFlyer } from "../api/client";
import type { Proyecto, RevisionFlyer } from "../api/types";
import estilos from "./PanelVersiones.module.css";

interface Props {
  token: string;
  proyectoId: string;
  disparadorRefresco: number;
  deshabilitado: boolean;
  onRestaurado: (proyecto: Proyecto) => void;
}

function fechaCorta(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

// Adaptado de PanelVersiones.tsx del hermano (video): mucho mas simple, ya
// que `GET /revisions` solo expone version+fecha (sin miniatura/estado de
// purga -- FlyerRevision guarda el JSON completo, no un archivo aparte). Se
// re-lista cuando `disparadorRefresco` cambia (el padre lo aumenta tras cada
// Guardar/Revertir, igual que el patron del hermano).
export function PanelVersiones({
  token,
  proyectoId,
  disparadorRefresco,
  deshabilitado,
  onRestaurado,
}: Props): JSX.Element {
  const [versiones, setVersiones] = useState<RevisionFlyer[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restaurando, setRestaurando] = useState<number | null>(null);

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    listarRevisionesFlyer(token, proyectoId)
      .then((r) => {
        if (!cancelado) {
          setVersiones(r);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelado) {
          setError(
            err instanceof Error ? err.message : "Error al listar versiones",
          );
        }
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [token, proyectoId, disparadorRefresco]);

  const restaurar = async (version: number): Promise<void> => {
    const ok = window.confirm(
      `Restaurar la version ${version}? Los cambios sin guardar del editor se descartaran.`,
    );
    if (!ok) return;
    setRestaurando(version);
    setError(null);
    try {
      const resp = await revertirFlyer(token, proyectoId, version);
      onRestaurado(resp.project);
    } catch (err) {
      if (err instanceof ErrorApi && err.status === 404) {
        setError(`La version ${version} ya no existe.`);
      } else {
        setError(err instanceof Error ? err.message : "No se pudo restaurar");
      }
    } finally {
      setRestaurando(null);
    }
  };

  return (
    <section className={estilos.panel} aria-labelledby="titulo-versiones">
      <div className={estilos.encabezado}>
        <h3 id="titulo-versiones">Versiones</h3>
      </div>

      {cargando && <p className={estilos.mensaje}>Cargando versiones...</p>}
      {error && (
        <p className={estilos.mensajeError} role="alert">
          {error}
        </p>
      )}
      {!cargando && versiones.length === 0 && (
        <p className={estilos.mensaje}>
          Sin versiones anteriores todavia (se crean automaticamente al guardar
          o revertir).
        </p>
      )}

      {versiones.length > 0 && (
        <ol className={estilos.lista}>
          {versiones.map((v) => (
            <li key={v.version} className={estilos.item}>
              <div className={estilos.itemInfo}>
                <span className={estilos.itemVersion}>v{v.version}</span>
                <span className={estilos.itemMeta}>
                  {fechaCorta(v.created_at)}
                </span>
              </div>
              <button
                type="button"
                className="boton boton-secundario"
                onClick={() => void restaurar(v.version)}
                disabled={deshabilitado || restaurando !== null}
              >
                {restaurando === v.version ? "Restaurando..." : "Restaurar"}
              </button>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
